/**
 * S3 arrival (`trigger.s3`) inspector: pick an S3 connection, a bucket, then
 * browse to the prefix to watch.
 *
 * Mirrors the Blob-arrival trigger — the two write the same
 * `pipeline_blob_watch` row server-side, with `container` holding the bucket.
 * Press Run on the pipeline to arm the watch, Stop to disarm it.
 */

import { Anchor, Button, Group, Loader, Select, Stack, Text } from "@mantine/core";
import { subfoldersOf, useS3Browse } from "./useS3Browse";
import { ConnectionRefSelect } from "./ConnectionRefSelect";
import { useConnectionRef } from "./useConnectionRef";

export function S3TriggerConfig({
  config,
  editable,
  onChange,
}: {
  config: Record<string, unknown>;
  editable: boolean;
  onChange: (key: string, value: unknown) => void;
}) {
  // A node names a connection id or a `@role`; everything here takes an id.
  const {
    connectionId,
    role,
    settled: roleSettled,
  } = useConnectionRef(String(config.connectionId ?? ""));
  const bucket = String(config.bucket ?? "");
  const prefix = String(config.prefix ?? "");
  const { connections, buckets, objects, loading } = useS3Browse(
    connectionId,
    bucket,
    prefix || null,
  );
  const folders = subfoldersOf(objects, prefix);
  const folderPrefix = prefix && !prefix.endsWith("/") ? `${prefix}/` : prefix;
  const segments = prefix ? prefix.replace(/\/$/, "").split("/") : [];

  return (
    <Stack gap="xs">
      <ConnectionRefSelect
        label="S3 connection"
        placeholder={connections.length ? "Pick a connection" : "No S3 connections set up"}
        data={connections}
        nothingFoundMessage="No S3 connections"
        connectionId={connectionId}
        role={role}
        settled={roleSettled}
        editable={editable}
        onChange={(v) => {
          const name = connections.find((c) => c.value === v)?.label ?? "";
          onChange("connectionId", v);
          onChange("connectionName", name);
          onChange("bucket", "");
          onChange("prefix", "");
        }}
      />

      {connectionId && (
        <Select
          label="Bucket"
          placeholder="Pick a bucket"
          data={buckets}
          value={bucket || null}
          disabled={!editable}
          searchable
          onChange={(v) => {
            onChange("bucket", v ?? "");
            onChange("prefix", "");
          }}
        />
      )}

      {connectionId && bucket && (
        <Stack gap={4}>
          <Text size="xs" c="dimmed">
            Folder to watch
          </Text>
          <Group gap={4} wrap="wrap">
            <Anchor size="xs" onClick={() => editable && onChange("prefix", "")}>
              {bucket}
            </Anchor>
            {segments.map((seg, i) => (
              <Group key={i} gap={4} wrap="nowrap">
                <Text size="xs" c="dimmed">
                  /
                </Text>
                <Anchor
                  size="xs"
                  onClick={() =>
                    editable && onChange("prefix", segments.slice(0, i + 1).join("/") + "/")
                  }
                >
                  {seg}
                </Anchor>
              </Group>
            ))}
          </Group>
          {loading ? (
            <Loader size="xs" />
          ) : folders.length > 0 ? (
            <Stack gap={2}>
              {folders.map((f) => (
                <Button
                  key={f}
                  variant="subtle"
                  size="compact-xs"
                  justify="flex-start"
                  disabled={!editable}
                  onClick={() => onChange("prefix", `${folderPrefix}${f}/`)}
                >
                  📁 {f}
                </Button>
              ))}
            </Stack>
          ) : (
            <Text size="xs" c="dimmed">
              No subfolders here — this folder will be watched.
            </Text>
          )}
          <Text size="xs" mt={4}>
            Watching:{" "}
            <Text span ff="monospace">
              {bucket}/{folderPrefix}
            </Text>
          </Text>
        </Stack>
      )}
    </Stack>
  );
}
